/**
 * Declarative `DataList` configs for the tenant Configuration & Backups screen.
 *
 * Two integrated `DataList` tables in **server mode** — the built-in
 * `useFetchListData` React Query fetches each page from the tenant config
 * endpoints through the shared `claimflowApiClient` (which injects the Bearer
 * token + `X-Tenant-ID`). Every column carries a **stable key** separate from
 * its display `label` / `headerTooltip`, so the tenant rule engine can rename /
 * hide / reorder columns without a rewrite (configurable-ready authoring).
 *
 * Custom cell renderers are registered once at module load (import side
 * effect), mirroring `taskListConfig.tsx`.
 */

import type { ReactNode } from 'react';

import { Badge, Text } from '@components/base/display';
import type { DataListConfig } from '@components/integrated/data-list';
import { dataListWorkflowRegistry } from '@components/integrated/data-list';

import {
  TRACKED_ENTITY_TYPES,
  entityTypeLabel,
  type ConfigActor,
} from '../api/configVersioningService';
import {
  CONFIG_HISTORY_LIST_ID,
  CONFIG_SNAPSHOT_LIST_ID,
} from '../hooks/configVersioning/queryKeys';

// ---------------------------------------------------------------------------
// Value helpers
// ---------------------------------------------------------------------------

function toStr(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function formatDateTime(value: unknown): string {
  const raw = toStr(value);
  if (raw === '') return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Static label maps
// ---------------------------------------------------------------------------

const ACTION_META: Record<
  string,
  { label: string; variant: 'success' | 'info' | 'error' }
> = {
  created: { label: 'Créé', variant: 'success' },
  updated: { label: 'Modifié', variant: 'info' },
  deleted: { label: 'Supprimé', variant: 'error' },
};

const SOURCE_LABELS: Record<string, string> = {
  api: 'API',
  seeder: 'Seeder',
  import: 'Import',
  revert: 'Restauration',
  system: 'Système',
};

const KIND_META: Record<
  string,
  { label: string; variant: 'default' | 'primary' | 'warning' }
> = {
  manual: { label: 'Manuelle', variant: 'primary' },
  scheduled: { label: 'Planifiée', variant: 'default' },
  pre_seed: { label: 'Avant seed', variant: 'warning' },
  pre_import: { label: 'Avant import', variant: 'warning' },
  pre_restore: { label: 'Avant restauration', variant: 'warning' },
};

// ---------------------------------------------------------------------------
// Custom cell renderers (registered once at module load)
// ---------------------------------------------------------------------------

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-datetime',
  (value): ReactNode => (
    <Text as="span" size="sm">
      {formatDateTime(value)}
    </Text>
  ),
);

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-actor',
  (value): ReactNode => {
    const actor = value as ConfigActor | null;
    if (!actor) {
      return (
        <Text as="span" size="sm" color="muted">
          Système
        </Text>
      );
    }
    return (
      <span className="flex flex-col">
        <Text as="span" size="sm" weight="medium">
          {actor.name}
        </Text>
        {actor.email && (
          <Text as="span" size="xs" color="muted">
            {actor.email}
          </Text>
        )}
      </span>
    );
  },
);

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-entity',
  (value, row): ReactNode => {
    const entityType = toStr(value);
    const entityKey = toStr(row.entity_key);
    return (
      <span className="flex flex-col">
        <Text as="span" size="sm" weight="medium">
          {entityTypeLabel(entityType)}
        </Text>
        {entityKey && (
          <Text as="span" size="xs" color="muted" truncate>
            {entityKey}
          </Text>
        )}
      </span>
    );
  },
);

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-action',
  (value): ReactNode => {
    const meta = ACTION_META[toStr(value)];
    if (!meta) {
      return (
        <Text as="span" size="sm">
          {toStr(value) || '—'}
        </Text>
      );
    }
    return (
      <Badge variant={meta.variant} size="sm">
        {meta.label}
      </Badge>
    );
  },
);

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-source',
  (value): ReactNode => {
    const source = toStr(value);
    return (
      <Text as="span" size="sm" color="muted">
        {SOURCE_LABELS[source] ?? (source || '—')}
      </Text>
    );
  },
);

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-kind',
  (value): ReactNode => {
    const meta = KIND_META[toStr(value)];
    if (!meta) {
      return (
        <Text as="span" size="sm">
          {toStr(value) || '—'}
        </Text>
      );
    }
    return (
      <Badge variant={meta.variant} size="sm">
        {meta.label}
      </Badge>
    );
  },
);

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-label',
  (value): ReactNode => {
    const label = toStr(value);
    return label === '' ? (
      <Text as="span" size="sm" color="muted">
        —
      </Text>
    ) : (
      <Text as="span" size="sm">
        {label}
      </Text>
    );
  },
);

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-config-entity-counts',
  (value): ReactNode => {
    const counts = (value ?? {}) as Record<string, number>;
    const entries = Object.entries(counts);
    const total = entries.reduce((sum, [, n]) => sum + n, 0);
    const breakdown = entries
      .map(([type, n]) => `${entityTypeLabel(type)}: ${String(n)}`)
      .join(' · ');
    return (
      <span className="flex flex-col">
        <Badge variant="default" size="sm">
          {total} entité{total > 1 ? 's' : ''}
        </Badge>
        {breakdown && (
          <Text as="span" size="xs" color="muted" truncate>
            {breakdown}
          </Text>
        )}
      </span>
    );
  },
);

// ---------------------------------------------------------------------------
// History list config
// ---------------------------------------------------------------------------

/**
 * `GET /config/history` — Laravel-paginated (`{ data, links, meta }`), newest
 * first. Columns keyed by stable keys (`changed_at`, `actor`, `entity`,
 * `action`, `source`). The `entity_type` toolbar filter offers exactly the
 * tracked types (the API 422s on anything else); the DataList param builder
 * omits the filter when "all" is selected, so no `entity_type=''` is ever sent.
 */
export function createConfigHistoryListConfig(): DataListConfig {
  return {
    id: CONFIG_HISTORY_LIST_ID,

    dataSource: {
      endpoint: { erpEndpoint: '/config/history', method: 'GET' },
      responseMapping: { itemsPath: 'data', totalPath: 'meta.total' },
      fieldMapping: {
        changed_at: 'created_at',
        actor: 'actor',
        entity: 'entity_type',
        action: 'action',
        source: 'source',
      },
    },

    columns: {
      changed_at: {
        label: 'Date',
        order: 1,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-datetime' },
        headerTooltip: 'Date et heure de la modification',
      },
      actor: {
        label: 'Auteur',
        order: 2,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-actor' },
        headerTooltip: 'Utilisateur à l’origine du changement',
      },
      entity: {
        label: 'Élément',
        order: 3,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-entity' },
        headerTooltip: 'Type et identifiant de l’élément de configuration',
      },
      action: {
        label: 'Action',
        order: 4,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-action' },
        headerTooltip: 'Nature de la modification',
      },
      source: {
        label: 'Source',
        order: 5,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-source' },
        headerTooltip: 'Origine de la modification',
      },
    },

    display: { title: 'Historique des modifications', hoverable: true },

    i18n: { emptyMessage: 'Aucune modification enregistrée.' },

    toolbar: {
      filters: [
        {
          id: 'entity_type',
          label: 'Type de configuration',
          type: 'select',
          placeholder: 'Tous les types',
          options: TRACKED_ENTITY_TYPES.map((type) => ({
            value: type,
            label: entityTypeLabel(type),
          })),
        },
      ],
    },

    actions: {
      rowActions: [
        {
          id: 'config-history-details',
          label: 'Détails',
          variant: 'ghost',
          handler: 'claimflow-config-details',
        },
        {
          id: 'config-history-revert',
          label: 'Restaurer',
          variant: 'ghost',
          handler: 'claimflow-config-revert',
        },
      ],
    },

    sorting: { enabled: false },

    pagination: {
      mode: 'pagination',
      pageSize: 20,
      pageParam: 'page',
      pageSizeParam: 'per_page',
      showPageSize: false,
      showTotal: true,
    },
  };
}

// ---------------------------------------------------------------------------
// Backups (snapshots) list config
// ---------------------------------------------------------------------------

/**
 * `GET /config/snapshots` — Laravel-paginated. Columns keyed by stable keys
 * (`created_at`, `kind`, `label`, `entity_counts`). A single Restore row action
 * opens the dry-run preview modal.
 */
export function createConfigBackupsListConfig(): DataListConfig {
  return {
    id: CONFIG_SNAPSHOT_LIST_ID,

    dataSource: {
      endpoint: { erpEndpoint: '/config/snapshots', method: 'GET' },
      responseMapping: { itemsPath: 'data', totalPath: 'meta.total' },
      fieldMapping: {
        created_at: 'created_at',
        kind: 'kind',
        label: 'label',
        entity_counts: 'entity_counts',
      },
    },

    columns: {
      created_at: {
        label: 'Date',
        order: 1,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-datetime' },
        headerTooltip: 'Date et heure de la capture',
      },
      kind: {
        label: 'Type',
        order: 2,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-kind' },
        headerTooltip: 'Origine de la sauvegarde',
      },
      label: {
        label: 'Libellé',
        order: 3,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-label' },
        headerTooltip: 'Libellé saisi lors de la capture',
      },
      entity_counts: {
        label: 'Contenu',
        order: 4,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-config-entity-counts' },
        headerTooltip: 'Nombre d’éléments par type dans la sauvegarde',
      },
    },

    display: { title: 'Sauvegardes', hoverable: true },

    i18n: { emptyMessage: 'Aucune sauvegarde disponible.' },

    actions: {
      rowActions: [
        {
          id: 'config-backup-restore',
          label: 'Restaurer',
          variant: 'ghost',
          handler: 'claimflow-config-restore',
        },
      ],
    },

    sorting: { enabled: false },

    pagination: {
      mode: 'pagination',
      pageSize: 20,
      pageParam: 'page',
      pageSizeParam: 'per_page',
      showPageSize: false,
      showTotal: true,
    },
  };
}
